/**
 * Отказ от писем о продукте: `/unsubscribe/:token`.
 *
 * Маршрут публичный, и это не послабление, а требование: человек, просящий
 * перестать ему писать, не обязан вспоминать пароль. Форма согласия на аудите
 * обещает «отказаться можно ссылкой в любом письме» — обещание держится здесь.
 *
 * Отказ выполняется САМ, без кнопки «подтвердите отказ». Лишний шаг здесь стоит
 * дороже, чем где-либо ещё: не дошедший до конца человек остаётся подписанным,
 * считая, что отписался, и следующее письмо читает как игнорирование просьбы.
 * Риск обратного — что по ссылке пройдёт почтовый сканер и отпишет человека
 * молча — несравним: подписаться обратно можно одной галочкой, а вернуть
 * доверие после письма «я же отписался» нечем.
 *
 * Ответ сервера одинаков для верного и неизвестного токена, поэтому и экран
 * один: «готово». Показать «такого токена нет» значило бы превратить страницу в
 * проверку, есть ли у нас такой человек.
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Mark } from '../ui/Mark';
import { Skeleton } from '../ui/state';
import { api } from '../api/client';

export function Unsubscribe() {
  const { token = '' } = useParams();
  // Пустой токен — сразу «готово»: показывать ожидание запроса, которого не
  // будет, значит рисовать работу там, где её нет. Начальное значение, а не
  // установка в эффекте: эффект синхронизирует состояние с внешним миром, а
  // «токена нет» известно до первой отрисовки.
  const [state, setState] = useState<'waiting' | 'done' | 'failed'>(
    token ? 'waiting' : 'done',
  );

  useEffect(() => {
    if (!token) return;
    const abort = new AbortController();
    void api
      .post('/public/unsubscribe', { token }, { signal: abort.signal, quiet: true })
      .then(() => setState('done'))
      // Отказ сети — НЕ «готово»: сервер просьбы не получил, и «писем больше
      // не будет» здесь стало бы ровно тем сценарием «я же отписался», от
      // которого экран защищает. Различается только доставка просьбы, не
      // судьба токена: ответ сервера одинаков для любого токена, и до
      // проверки «есть ли такой человек» этот экран не опускается.
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        setState('failed');
      });
    return () => abort.abort();
  }, [token]);
  const done = state === 'done';

  return (
    <main className="auth">
      <div className="auth-card">
        <a className="brand" href="https://seeno.ru/">
          <Mark />
          seeno
        </a>
        {done ? (
          <>
            <h1>Писем больше не будет</h1>
            <p className="lede">
              Мы убрали ваш адрес из рассылки о продукте. Отчёты аудитов, которые вы
              запускали, и служебные письма про вход и оплату это не трогает.
            </p>
            <p className="hint">
              Передумаете — поставьте отметку при следующем запуске аудита.{' '}
              <Link to="/audit">Запустить аудит</Link>
            </p>
          </>
        ) : state === 'failed' ? (
          <>
            <h1>Просьба до нас не дошла</h1>
            <p className="lede">
              Запрос не добрался до сервера — возможно, прервалась связь. Вы пока не
              отписаны: откройте ссылку из письма ещё раз, отказ сработает сразу.
            </p>
          </>
        ) : (
          <Skeleton rows={2} />
        )}
      </div>
    </main>
  );
}
